<template>
  <div class="ud-page">
    <div class="ud-container">

      <!-- Page header -->
      <div class="ud-header">
        <div class="ud-header-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <h1 class="ud-header-title">تعديل البيانات الشخصية</h1>
        <p class="ud-header-sub">يمكنك تحديث بياناتك في أي وقت</p>
      </div>

      <!-- Subscriptions summary (Feature 9) -->
      <ProfileSubscriptions />

      <form v-if="!pageLoading" @submit.prevent="sendForm" novalidate class="ud-form">

        <!-- Card 1: Personal Information -->
        <section class="ud-card">
          <div class="ud-card-head">
            <span class="ud-card-icon"><i class="pi pi-user"></i></span>
            <h2 class="ud-card-title">المعلومات الشخصية</h2>
          </div>
          <div class="ud-card-divider"></div>
          <div class="ud-grid">

            <!-- Student name -->
            <div class="ud-field" :class="{ 'ud-field--error': v$.student_name.$error }">
              <label class="ud-label"><i class="pi pi-user"></i> اسم الطالب <span class="ud-req">*</span></label>
              <div class="ud-input-wrap">
                <span class="ud-icon"><i class="pi pi-user"></i></span>
                <input type="text" v-model="student_name" class="ud-input" placeholder="الاسم كاملاً" />
              </div>
              <span class="ud-error" v-if="v$.student_name.$error">برجاء إدخال اسم الطالب</span>
            </div>

            <!-- Address -->
            <div class="ud-field">
              <label class="ud-label"><i class="pi pi-map-marker"></i> العنوان <span class="ud-opt">(اختياري)</span></label>
              <div class="ud-input-wrap">
                <span class="ud-icon"><i class="pi pi-map-marker"></i></span>
                <input type="text" v-model="address" class="ud-input" placeholder="المدينة / الحي" />
              </div>
            </div>

          </div>
        </section>

        <!-- Card 2: Contact Information -->
        <section class="ud-card">
          <div class="ud-card-head">
            <span class="ud-card-icon"><i class="pi pi-phone"></i></span>
            <h2 class="ud-card-title">معلومات التواصل</h2>
          </div>
          <div class="ud-card-divider"></div>
          <div class="ud-grid">

            <!-- Student phone -->
            <div class="ud-field" :class="{ 'ud-field--error': v$.student_phone.$error }">
              <label class="ud-label"><i class="pi pi-phone"></i> رقم هاتف الطالب <span class="ud-req">*</span></label>
              <div class="ud-input-wrap">
                <span class="ud-icon"><i class="pi pi-phone"></i></span>
                <input type="tel" v-model="student_phone" class="ud-input" placeholder="01XXXXXXXXX" dir="ltr" v-phone inputmode="tel" />
              </div>
              <span class="ud-error" v-if="v$.student_phone.$error">برجاء إدخال رقم هاتف الطالب</span>
            </div>

            <!-- WhatsApp number (optional) -->
            <div class="ud-field" :class="{ 'ud-field--error': v$.whatsapp_number.$error }">
              <label class="ud-label"><i class="pi pi-whatsapp"></i> رقم الواتساب <span class="ud-opt">(اختياري)</span></label>
              <div class="ud-input-wrap">
                <span class="ud-icon"><i class="pi pi-whatsapp"></i></span>
                <input type="tel" v-model="whatsapp_number" class="ud-input" placeholder="أدخل رقم الواتساب" dir="ltr" v-phone inputmode="tel" />
              </div>
              <span class="ud-error" v-if="v$.whatsapp_number.$error">رقم الواتساب يجب أن يتكون من 11 رقم ويبدأ بـ 01</span>
            </div>

            <!-- Parent phone -->
            <div class="ud-field" :class="{ 'ud-field--error': v$.parent_phone.$error }">
              <label class="ud-label"><i class="pi pi-phone"></i> رقم هاتف ولي الأمر <span class="ud-req">*</span></label>
              <div class="ud-input-wrap">
                <span class="ud-icon"><i class="pi pi-phone"></i></span>
                <input type="tel" v-model="parent_phone" class="ud-input" placeholder="01XXXXXXXXX" dir="ltr" v-phone inputmode="tel" />
              </div>
              <span class="ud-error" v-if="v$.parent_phone.$error">برجاء إدخال رقم هاتف ولي الأمر</span>
            </div>

          </div>

          <!-- Phone change warning -->
          <div class="ud-phone-warn" v-if="student_phone && student_phone != user?.phone">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="ud-warn-icon"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span>سيتم تغيير كلمة مرورك إلى رقم هاتفك الجديد: <strong>{{ student_phone }}</strong></span>
          </div>
        </section>

        <!-- Card 3: Education -->
        <section class="ud-card">
          <div class="ud-card-head">
            <span class="ud-card-icon"><i class="pi pi-graduation-cap"></i></span>
            <h2 class="ud-card-title">نظام التعليم</h2>
          </div>
          <div class="ud-card-divider"></div>
          <div class="ud-field" :class="{ 'ud-field--error': v$.selected_type.$error }">
            <div class="ud-segment">
              <button
                type="button"
                v-for="t in types"
                :key="t.id"
                class="ud-segment-btn"
                :class="{ 'ud-segment-btn--active': selected_type?.id === t.id }"
                @click="selected_type = t"
              >
                {{ t.name }}
              </button>
            </div>
            <span class="ud-error" v-if="v$.selected_type.$error">برجاء اختيار نظام التعليم</span>
          </div>
        </section>

        <!-- Spacer so the last card is never hidden behind the sticky footer -->
        <!-- <div class="ud-footer-spacer"></div> -->

        <!-- Sticky save footer -->
        <div class="ud-sticky-footer">
          <div class="ud-sticky-footer-inner">
            <button type="submit" class="ud-submit" :disabled="formSending">
              <Transition name="ud-icon-swap" mode="out-in">
                <span v-if="formSending" key="sending" class="ud-submit-content">
                  <span class="ud-spinner"></span> جارٍ الحفظ...
                </span>
                <span v-else key="idle" class="ud-submit-content">
                  <i class="pi pi-save"></i> حفظ التعديلات
                </span>
              </Transition>
            </button>
          </div>
        </div>

      </form>

      <!-- Page skeleton -->
      <div v-else class="ud-form">
        <div class="ud-card ud-skeleton" v-for="n in 3" :key="n">
          <div class="skel skel-title"></div>
          <div class="ud-grid" style="margin-top:1.25rem">
            <div class="skel skel-field"></div>
            <div class="skel skel-field"></div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import useVuelidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import ProfileSubscriptions from '@/components/subscriptions/ProfileSubscriptions.vue';

// Mirrors the backend's RegistrationFormSettings::mobilePhoneRules(false) exactly
// (nullable + digits:11 + starts_with:01) — optional field, but if a value IS
// entered it must be a full 11-digit Egyptian mobile number. Empty/falsy always
// passes (helpers.req) so this never blocks submission when the field is unused.
const egyptianMobileIfFilled = helpers.withMessage(
  "رقم الواتساب يجب أن يتكون من 11 رقم ويبدأ بـ 01",
  (value) => !helpers.req(value) || /^01[0-9]{9}$/.test(value)
);

export default {
  components: { ProfileSubscriptions },
  name: "user-edit",
  data() {
    return {
      v$: useVuelidate(),
      student_name: "",
      student_phone: null,
      parent_phone: null,
      whatsapp_number: null,
      address: "",
      math_degree: null,
      certificate_image: "",
      imageEntry: "",
      fileData: "",
      formSending: false,
      pageLoading: true,
      selected_gender: null,
      selected_type: null,
      user: null,
      genders: [
        { name: 'ولد', id: 1 },
        { name: 'بنت', id: 2 },
      ],
      types: [
        { name: 'عام', id: 1 },
        { name: 'أزهر', id: 2 },
      ],
      user_id: JSON.parse(localStorage.getItem("user")).id,
    };
  },
  validations() {
    return {
      student_name: { required },
      student_phone: { required },
      parent_phone: { required },
      whatsapp_number: { egyptianMobileIfFilled },
      selected_type: { required },
    };
  },
  methods: {
    onFileChange(event) {
      this.fileData = event.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(this.fileData);
      reader.onload = () => (this.imageEntry = reader.result);
    },
    onlyNumbers(event) {
      const char = String.fromCharCode(event.which);
      if (!/[0-9٠-٩]/.test(char)) { event.preventDefault(); return; }
      const value = event.target.value;
      const digitsCount = (value.match(/[0-9٠-٩]/g) || []).length;
      if (digitsCount >= 11) event.preventDefault();
    },
    toEnglishDigits(str) {
      if (!str) return str;
      return str.replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
    },
    sendForm() {
      this.v$.$validate();
      if (this.v$.$error) return;

      const formData = new FormData();
      formData.append('user_id', this.user_id);
      formData.append('student_name', this.student_name);
      formData.append('student_phone', this.toEnglishDigits(this.student_phone));
      formData.append('parent_phone', this.toEnglishDigits(this.parent_phone));
      if (this.whatsapp_number) formData.append('whatsapp_number', this.toEnglishDigits(this.whatsapp_number));
      if (this.address) formData.append('address', this.address);
      if (this.selected_gender) formData.append('gender', this.selected_gender.id);
      formData.append('education_type', this.selected_type.id);
      if (this.math_degree) formData.append('math_degree', this.math_degree);
      if (this.fileData instanceof File) formData.append("certificate_image", this.fileData);

      this.formSending = true;
      axios.post('/update_user', formData)
        .then(() => {
          Swal.fire({
            position: "center", icon: "success", title: "تم تحديث بيانات الطالب بنجاح",
            text: this.student_phone != this.user?.phone
              ? "تم تغيير كلمة المرور لرقم هاتفك الجديد " + this.student_phone
              : "",
          });
        })
        .catch((error) => {
          Swal.fire({ position: "center", icon: "error", title: error.response?.data?.message });
        })
        .finally(() => { this.formSending = false; });
    },
    fetchUserData() {
      const formData = new FormData();
      formData.append('user_id', this.user_id);
      axios.post('/get_user', formData)
        .then((response) => {
          this.user = response.data.user;
          this.student_name = this.user?.name;
          this.student_phone = this.user?.phone;
          this.parent_phone = this.user?.parent_phone;
          this.whatsapp_number = this.user?.whatsapp_number;
          this.address = this.user?.address;
          this.math_degree = response.data.math_degree;
          this.gender = this.user?.gender;
          this.selected_gender = this.genders.find(g => g.id === this.user?.gender);
          this.education_type = this.user?.education_type;
          this.selected_type = this.types.find(t => t.id === this.user?.education_type);
          this.education_level = this.user?.education_level;
          this.certificate_image = this.user?.certificate_image;
        })
        .catch(() => { this.user = {}; })
        .finally(() => { this.pageLoading = false; });
    },
  },
  mounted() {
    this.fetchUserData();
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.ud-page {
  min-height: calc(100vh - 70px);
  background: #F5F7FB;
  font-family: 'Cairo', sans-serif;
  direction: rtl;
  padding: 2rem 0 2rem;
}
.ud-container { max-width: 860px; margin: 0 auto; padding: 0 1.25rem; }

/* ── Header ── */
.ud-header { text-align: center; padding: 2rem 1rem 1.75rem; }
.ud-header-icon {
  width: 70px; height: 70px;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  border-radius: 22px;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1rem;
  box-shadow: 0 16px 40px rgba(91,95,239,0.3);
}
.ud-header-icon svg { width: 34px; height: 34px; stroke: #fff; }
.ud-header-title {
  font-size: 1.8rem; font-weight: 800; color: #1A1A1A; margin-bottom: 0.4rem;
}
.ud-header-sub { font-size: 0.9rem; color: #6B7280; }

/* ── Form / card stack — 24px between cards (requirement 4) ── */
.ud-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 32px;
}

/* ── Card — rounded 18-22px, subtle shadow, white bg, fades in on mount ── */
.ud-card {
  background: #fff;
  border-radius: 20px;
  padding: 1.75rem;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.05);
  border: 1px solid rgba(15, 23, 42, 0.04);
  opacity: 0;
  animation: udCardIn 0.45s ease forwards;
}
.ud-form .ud-card:nth-of-type(1) { animation-delay: 0.02s; }
.ud-form .ud-card:nth-of-type(2) { animation-delay: 0.09s; }
.ud-form .ud-card:nth-of-type(3) { animation-delay: 0.16s; }
@keyframes udCardIn {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .ud-card { animation: none; opacity: 1; }
}

/* ── Card header: icon + title + divider ── */
.ud-card-head { display: flex; align-items: center; gap: 0.65rem; margin-bottom: 0.9rem; }
.ud-card-icon {
  width: 38px; height: 38px; flex-shrink: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem;
  box-shadow: 0 6px 16px rgba(91,95,239,0.28);
}
.ud-card-title { font-size: 1.25rem; font-weight: 600; color: #1A1A1A; }
.ud-card-divider {
  height: 1px;
  background: linear-gradient(to left, rgba(91,95,239,0.16), rgba(91,95,239,0));
  margin-bottom: 1.25rem;
}

/* ── Responsive 2-column grid (requirement 3) — 16px between inputs ── */
.ud-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

/* ── Field ── */
.ud-field { display: flex; flex-direction: column; gap: 0.4rem; }
.ud-label {
  font-size: 13px; font-weight: 500; color: #6B7280;
  display: flex; align-items: center; gap: 0.4rem;
}
.ud-label i { font-size: 12px; color: #9CA3AF; }
.ud-req { color: #EF4444; font-size: 0.9rem; }
.ud-opt { color: #22C55E; font-size: 0.75rem; font-weight: 600; }

.ud-input-wrap { position: relative; display: flex; align-items: center; }
.ud-icon {
  position: absolute; right: 14px;
  width: 18px; height: 18px;
  color: #9CA3AF; pointer-events: none;
  display: flex; align-items: center; justify-content: center;
  transition: color 0.2s;
}
.ud-icon svg { width: 16px; height: 16px; }
.ud-icon i { font-size: 15px; line-height: 1; }

.ud-input {
  width: 100%;
  padding: 0.85rem 2.6rem 0.85rem 1rem;
  border: 1.5px solid #E5E7EB;
  border-radius: 14px;
  font-family: 'Cairo', sans-serif;
  font-size: 16px; color: #1A1A1A;
  background: #F9FAFB;
  outline: none;
  box-shadow: 0 1px 2px rgba(15,23,42,0.03);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, transform 0.15s ease;
}
.ud-input:hover { border-color: #C7CBF5; background: #fff; }
.ud-input:focus {
  border-color: #5B5FEF;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(91,95,239,0.12);
  transform: translateY(-1px);
}
.ud-input-wrap:has(.ud-input:focus) .ud-icon { color: #5B5FEF; }
.ud-field--error .ud-input { border-color: #EF4444; box-shadow: 0 0 0 4px rgba(239,68,68,0.08); }
.ud-field--error .ud-icon { color: #EF4444; }
.ud-error { font-size: 0.76rem; color: #EF4444; font-weight: 600; }

/* ── Education System: segmented control (requirement 6) ── */
.ud-segment {
  display: flex;
  background: #F3F4F6;
  border-radius: 14px;
  padding: 4px;
  gap: 4px;
}
.ud-segment-btn {
  flex: 1;
  padding: 0.7rem 1rem;
  border: none;
  border-radius: 10px;
  background: transparent;
  font-family: 'Cairo', sans-serif;
  font-size: 0.92rem; font-weight: 700;
  color: #6B7280; cursor: pointer;
  transition: all 0.22s ease;
}
.ud-segment-btn:hover:not(.ud-segment-btn--active) { color: #5B5FEF; }
.ud-segment-btn--active {
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  color: #fff;
  box-shadow: 0 6px 16px rgba(91,95,239,0.32);
}

/* ── Phone warning ── */
.ud-phone-warn {
  display: flex; align-items: flex-start; gap: 0.65rem;
  background: rgba(245,158,11,0.08);
  border: 1.5px solid rgba(245,158,11,0.25);
  border-radius: 13px;
  padding: 0.8rem 1rem;
  margin-top: 1.25rem;
  color: #92400e; font-size: 0.85rem; font-weight: 600;
  line-height: 1.5;
}
.ud-warn-icon { width: 18px; height: 18px; flex-shrink: 0; stroke: #D97706; margin-top: 2px; }

/* ── Sticky save footer (requirement 7) ── */
.ud-footer-spacer { height: 96px; }
.ud-sticky-footer {
  /* position: fixed; */
  left: 0; right: 0; bottom: 0;
  z-index: 40;
  background: rgba(245, 247, 251, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  padding: 0.9rem 1.25rem calc(0.9rem + env(safe-area-inset-bottom));
}
.ud-sticky-footer-inner { max-width: 860px; margin: 0 auto; }
.ud-submit {
  width: 100%;
  padding: 0.95rem 2rem;
  border: none; border-radius: 16px;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  color: #fff;
  font-family: 'Cairo', sans-serif;
  font-size: 1rem; font-weight: 800;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(91,95,239,0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease, opacity 0.2s ease;
  display: flex; align-items: center; justify-content: center;
  min-height: 52px;
}
.ud-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 18px 40px rgba(91,95,239,0.42); }
.ud-submit:active:not(:disabled) { transform: translateY(0); }
.ud-submit:disabled { opacity: 0.75; cursor: not-allowed; }

.ud-submit-content { display: flex; align-items: center; gap: 0.6rem; }

/* Icon/label swap when formSending toggles — the "success" moment is this
   fade+scale back to the save icon/label right after a completed request. */
.ud-icon-swap-enter-active,
.ud-icon-swap-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.ud-icon-swap-enter-from,
.ud-icon-swap-leave-to { opacity: 0; transform: scale(0.92); }

.ud-spinner {
  width: 20px; height: 20px;
  border: 3px solid rgba(255,255,255,0.35);
  border-top-color: #fff; border-radius: 50%;
  animation: udSpin 0.8s linear infinite; display: inline-block;
}
@keyframes udSpin { to { transform: rotate(360deg); } }

/* ── Skeleton ── */
.ud-skeleton { pointer-events: none; animation: none; opacity: 1; }
.skel {
  background: linear-gradient(90deg,#f0f0f0 25%,#e6e6e6 50%,#f0f0f0 75%);
  background-size: 200% 100%;
  animation: udSkel 1.4s infinite; border-radius: 10px;
}
@keyframes udSkel { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skel-title { height: 20px; width: 40%; border-radius: 8px; }
.skel-field { height: 52px; border-radius: 13px; }

@media (max-width: 640px) {
  .ud-form { padding-top: 24px; }
  .ud-card { padding: 1.5rem 1.25rem; border-radius: 18px; }
  .ud-grid { grid-template-columns: 1fr; }
  .ud-sticky-footer { padding: 0.8rem 1rem calc(0.8rem + env(safe-area-inset-bottom)); }
}
</style>
